import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkPortalOutlet, ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { TranslocoModule } from '@jsverse/transloco';
import { QaiButtonComponent, QaiDialogComponent, QaiLoadingComponent } from '@qai/ui';
import { TestSimulationLauncherService } from 'src/app/services/self-serve/test-simulation-launcher.service';
import { CallService } from 'src/app/services/learner/call.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { ResultsConversationalComponentNew } from 'src/app/pages/learner/results/results-conversational-new/results-conversational-new.component';

type ScoreLastSessionModalState = 'loading' | 'notFound' | 'error' | 'results';

@Component({
  selector: 'score-last-session-modal',
  standalone: true,
  templateUrl: './score-last-session-modal.component.html',
  styleUrls: ['./score-last-session-modal.component.scss'],
  imports: [
    CommonModule,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiLoadingComponent,
    PortalModule,
    TranslocoModule,
  ],
})
export class ScoreLastSessionModalComponent implements OnChanges {
  @ViewChild('scoreLastSessionDialog') dialogRef: QaiDialogComponent | undefined;
  @ViewChild('resultsPortalOutlet', { read: CdkPortalOutlet })
  resultsPortalOutlet: CdkPortalOutlet | undefined;

  open = input<boolean>(false);
  simulationName = input<string>('');
  callUuid = input<string>('');
  moduleUuid = input<string>('');
  replicaId = input<string>('');

  openChange = output<boolean>();
  confirm = output<void>();
  closeModal = output<void>();

  state = signal<ScoreLastSessionModalState>('loading');
  isLaunchingTestSimulation = signal(false);

  private isClosing = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly testSimulationLauncherService = inject(TestSimulationLauncherService);
  private readonly callService = inject(CallService);

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.open()) {
      this.dialogRef?.showModal();
      if (changes['open']) {
        this.triggerScoring();
      }
    } else {
      this.detachResults();
      this.dialogRef?.close();
    }
  }

  handleTestSimulation(): void {
    const callUuid = this.callUuid();
    const moduleUuid = this.moduleUuid();
    const currentUserUuid = SelfServeLocalStorageUtil.getCurrentUser().userUuid;

    if (!callUuid || !moduleUuid || !currentUserUuid || !this.replicaId() || this.isLaunchingTestSimulation()) {
      console.warn('Test simulation launch skipped: missing required context or already launching', {
        callUuid,
        moduleUuid,
        currentUserUuid,
        hasReplica: !!this.replicaId(),
        isLaunchingTestSimulation: this.isLaunchingTestSimulation(),
      });
      return;
    }

    this.isLaunchingTestSimulation.set(true);

    this.testSimulationLauncherService.launchTestSimulation(callUuid, moduleUuid, currentUserUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLaunchingTestSimulation.set(false);
          this.confirm.emit();
        },
        error: (err) => {
          this.isLaunchingTestSimulation.set(false);
          console.error('Test simulation launch failed', err);
        },
      });
  }

  handleClose(): void {
    this.setClosedState();
  }

  private triggerScoring(): void {
    const callUuid = this.callUuid();
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    this.state.set('loading');
    this.detachResults();

    if (!callUuid) {
      console.warn('Score last session: missing callUuid, cannot trigger scoring');
      this.state.set('error');
      return;
    }

    this.callService
      .triggerScoring(callUuid, accountUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.state.set('results');
          this.attachResults(res.practiceCallUuid);
        },
        error: (err) => {
          console.error('Trigger scoring failed', err);
          this.state.set(err?.status === 404 ? 'notFound' : 'error');
        },
      });
  }

  private attachResults(practiceCallUuid: string): void {
    if (!this.resultsPortalOutlet) {
      return;
    }

    const portal = new ComponentPortal(ResultsConversationalComponentNew);
    const componentRef = this.resultsPortalOutlet.attachComponentPortal(portal);
    componentRef.instance.practiceCallUuid = practiceCallUuid;
    componentRef.instance.embedded = true;
  }

  private detachResults(): void {
    if (this.resultsPortalOutlet?.hasAttached()) {
      this.resultsPortalOutlet.detach();
    }
  }

  private setClosedState(): void {
    if (!this.open() || this.isClosing) {
      return;
    }

    this.isClosing = true;
    this.detachResults();
    this.closeModal.emit();
    this.dialogRef?.close();
    this.openChange.emit(false);
    this.cdRef.detectChanges();
    this.isClosing = false;
  }
}
